import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

const MODALS = [
  'settings',
  'add_user',
  'update_user',
  'add_project',
  'update_project',
  'add_building',
  'update_building',
  'add_floor',
  'update_floor',
  'manage_zones',
  'add_company',
  'update_company',
  'add_task',
  'update_task',
  'add_task_template',
  'update_task_template',
  'update_task_localization',
  'add_sequence',
  'update_sequence',
  'add_plan',
  'update_plan',
  'add_document',
  'update_document',
  'add_project_user',
  'update_project_user',
  'add_form_model',
  'update_form_model',
  'add_form',
  'update_form',
  'update_form_localization',
  'submit_form',
  'add_observation',
  'update_observation',
  'update_observation_localization',
  'update_project_email_settings',
] as const

export type ModalType = (typeof MODALS)[number]

function getDefaultModals(data: string | null): ModalType[] {
  try {
    if (data) {
      const keys = JSON.parse(data) as string[]
      const formattedModals = keys.filter((key) => MODALS.indexOf(key as any) != -1) as ModalType[]
      return formattedModals
    }
  } catch (error) {}

  return []
}

export const useModal = (): {
  currentModals: ModalType[]
  displayModal: (modal: ModalType, params?: { [key: string]: string | undefined | null }) => void
  hideModal: (modal: ModalType, params?: { [key: string]: string | undefined | null }) => void
} => {
  const [searchParams, setSearchParams] = useSearchParams()

  const modalSearchParam = searchParams.get('modals')

  const currentModals: ModalType[] = getDefaultModals(modalSearchParam)

  const displayModal = (modal: ModalType, params?: { [key: string]: string | undefined | null }) => {
    if (currentModals.indexOf(modal) != -1) {
      return
    }

    if (params) {
      setSearchParams({
        ...Object.fromEntries([...searchParams]),
        modals: JSON.stringify([...currentModals, modal]),
        ...params,
      })
    } else {
      setSearchParams({ ...Object.fromEntries([...searchParams]), modals: JSON.stringify([...currentModals, modal]) })
    }
  }

  const hideModal = (_modal: ModalType, params?: { [key: string]: string | undefined | null }) => {
    const filteredModals = currentModals.filter((modal) => modal != _modal)

    const paramKeys = Object.keys(params || {})

    const _searchParams = [...searchParams].filter((p) => paramKeys.indexOf(p[0]) == -1)

    if (filteredModals.length == 0) {
      setSearchParams({ ...Object.fromEntries(_searchParams.filter((p) => p[0] != 'modals')) })
    } else {
      setSearchParams({ ...Object.fromEntries(_searchParams), modals: JSON.stringify(filteredModals) })
    }
  }

  return { currentModals, displayModal, hideModal }
}

type ModalCallback = () => void
type ModalCallbacks = {
  [K in ModalType]?: ModalCallback
}

export const useModalWithCallback = () => {
  const { currentModals, displayModal, hideModal } = useModal()
  const callbacksRef = useRef<ModalCallbacks>({})
  const previousModalsRef = useRef<ModalType[]>([])

  useEffect(() => {
    previousModalsRef.current.forEach((modal) => {
      if (!currentModals.includes(modal)) {
        const callback = callbacksRef.current[modal]
        if (callback) {
          callback()
          delete callbacksRef.current[modal]
        }
      }
    })

    previousModalsRef.current = currentModals
  }, [currentModals])

  const displayModalWithCallback = (
    modal: ModalType,
    paramsOrCallback?: { [key: string]: string | undefined | null } | ModalCallback,
    callback?: ModalCallback,
  ) => {
    if (typeof paramsOrCallback === 'function') {
      callbacksRef.current[modal] = paramsOrCallback
      displayModal(modal)
    } else if (paramsOrCallback && callback) {
      callbacksRef.current[modal] = callback
      displayModal(modal, paramsOrCallback)
    } else if (paramsOrCallback) {
      displayModal(modal, paramsOrCallback)
    } else {
      displayModal(modal)
    }
  }

  return {
    currentModals,
    displayModal: displayModalWithCallback,
    hideModal,
  }
}
